import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TokenDatapoint } from '../api/types.js';

interface TokenUsageChartProps {
  datapoints: TokenDatapoint[];
}

function formatTokenCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
}

function formatDate(timestamp: string): string {
  const d = new Date(timestamp);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

interface ChartDatapoint {
  date: string;
  fullDate: string;
  inputTokens: number;
  outputTokens: number;
}

export function TokenUsageChart({ datapoints }: TokenUsageChartProps) {
  const data: ChartDatapoint[] = datapoints.map((dp) => ({
    date: formatDate(dp.timestamp),
    fullDate: new Date(dp.timestamp).toLocaleDateString(),
    inputTokens: dp.inputTokens,
    outputTokens: dp.outputTokens,
  }));

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center" style={{ color: 'var(--color-text-3)' }}>
        No token usage data for this period.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--color-text-3)' }} />
        <YAxis tickFormatter={formatTokenCount} tick={{ fontSize: 12, fill: 'var(--color-text-3)' }} />
        <Tooltip
          contentStyle={{ backgroundColor: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)' }}
          labelStyle={{ color: 'var(--color-text-2)' }}
          formatter={(value, name) => [
            formatTokenCount(Number(value ?? 0)),
            name === 'inputTokens' ? 'Input' : 'Output',
          ]}
          labelFormatter={(_label, payload) =>
            (payload?.[0] as { payload?: ChartDatapoint } | undefined)?.payload?.fullDate ?? ''
          }
        />
        <Legend formatter={(value: string) => (value === 'inputTokens' ? 'Input tokens' : 'Output tokens')} wrapperStyle={{ color: 'var(--color-text-2)', fontSize: 12 }} />
        <Bar dataKey="inputTokens" stackId="tokens" fill="var(--color-brand)" radius={[0, 0, 0, 0]} />
        <Bar dataKey="outputTokens" stackId="tokens" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
